import { Account, Client, Databases, Storage } from 'appwrite';

import { appwriteConfig } from '@/config/appwrite';

/**
 * The single place where the Appwrite client is configured — the web
 * counterpart of the Flutter app's `service_locator.dart`. Everything else in
 * the app depends on these instances rather than building its own client.
 *
 * The Appwrite Web SDK persists the session itself (a cookie, falling back to
 * a `cookieFallback` entry in localStorage when third-party cookies are
 * blocked), so there is no separate local "logged in" flag to keep in sync —
 * `account.get()` is the source of truth.
 */
export const client = new Client()
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId);

export const account = new Account(client);
export const databases = new Databases(client);
export const storage = new Storage(client);

/**
 * Downloads a storage file as raw bytes, authenticated as the current user.
 *
 * `storage.getFileDownload()` in the Web SDK returns a *URL string*, not the
 * bytes — and fetching that URL by hand would drop the session, because the
 * SDK's auth lives in `credentials: 'include'` plus the `X-Fallback-Cookies`
 * header it adds for browsers that block third-party cookies. Routing the
 * request back through `client.call` keeps both, and asks for an ArrayBuffer
 * instead of the default JSON parse.
 */
export async function downloadFileBytes(bucketId: string, fileId: string): Promise<ArrayBuffer> {
  const url = new URL(storage.getFileDownload(bucketId, fileId));
  const result: unknown = await client.call('GET', url, {}, {}, 'arrayBuffer');

  if (result instanceof ArrayBuffer) return result;
  // Some runtimes hand back a view rather than the buffer itself.
  if (ArrayBuffer.isView(result)) {
    const view = result as ArrayBufferView;
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
  }
  if (result instanceof Blob) return await result.arrayBuffer();

  throw new Error('Unexpected response while downloading the file.');
}
